import React, { useState, useEffect, useRef } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import Button from "./Button";

interface CarouselContainerProps {
  children?: React.ReactNode[];
  className?: string;
  showButtonsUp?: boolean;
  showButtonsDown?: boolean;
  paginationType?: "number" | "dot";
  onSlideChange?: (index: number) => void;
}

export default function Container({
  children,
  className,
  showButtonsUp,
  showButtonsDown,
  paginationType = "number",
  onSlideChange,
}: CarouselContainerProps) {
  const [curr, setCurr] = useState(0);
  const sliderRef = useRef(null);

  const totalCards = React.Children.count(children);
  const showNavigation = totalCards > 1;

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      const scrollPosition = slider.scrollLeft;
      const cardWidth = slider.offsetWidth;
      const index = Math.round(scrollPosition / cardWidth);
      setCurr(index);
      onSlideChange?.(index);
    };

    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, [onSlideChange]);

  const scrollToCard = (index) => {
    const slider = sliderRef.current;
    if (slider) {
      const cardWidth = slider.offsetWidth;
      slider.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
    }
  };

  const goToPrevious = () => {
    if (curr > 0) {
      scrollToCard(curr - 1);
    }
  };

  const goToNext = () => {
    if (curr < React.Children.count(children) - 1) {
      scrollToCard(curr + 1);
    }
  };

  return (
    <div className={`${className}`}>
      <div className="relative flex items-center">
        <div
          ref={sliderRef}
          className="flex flex-row w-full h-full overflow-x-auto overflow-y-hidden scroll scroll-smooth no-scrollbar space-x-2"
          style={{
            scrollSnapType: "x mandatory",
          }}
        >
          {React.Children.map(children, (child) => (
            <div
              style={{ scrollSnapAlign: "start", flexShrink: 0, width: "100%" }}
              className="overflow-hidden whitespace-normal"
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      <div className="h-8 my-2 flex justify-center items-center">
        {showNavigation ? (
          <div className="flex items-center gap-2">
            {(showButtonsUp || showButtonsDown) && (
              <Button.Circle
                variant="custom"
                onClick={goToPrevious}
                disabled={curr === 0}
                aria-label="Previous slide"
                className="p-1.5 text-gray-500 bg-gray-100 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </Button.Circle>
            )}
            <div className="flex items-center justify-center gap-2 px-1">
              {paginationType === "number" ? (
                <span className="text-sm">
                  {curr + 1}/{totalCards}
                </span>
              ) : (
                <>
                  {React.Children.map(children, (_, i) => (
                    <div key={i} className="static transition-all">
                      <div
                        onClick={() => scrollToCard(i)}
                        className={`cursor-pointer transition-all w-3 h-3 rounded-full
                          ${curr === i ? "bg-electrum-300" : "bg-electrum-100"}`}
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
            {(showButtonsUp || showButtonsDown) && (
              <Button.Circle
                variant="custom"
                onClick={goToNext}
                disabled={curr === React.Children.count(children) - 1}
                aria-label="Next slide"
                className="p-1.5 text-gray-500 bg-gray-100 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </Button.Circle>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
